import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUserContext } from "../hooks/UserContext";
import { useNotification } from "../hooks/NotificationContext";
import { Cliente } from "../models/ClienteModel";
import { Producto } from "../models/ProductoModel";
import { LineaVenta } from "../models/LineaVentaModel";
import { fetchGetClientes } from "../services/clienteService";
import { Alert, Button, CircularProgress, Dialog, DialogTitle, Grid, Box, Typography } from "@mui/material";
import DynamicGrid from "../components/DynamicTable";
import { fetchGuardarVenta } from "../services/ventaService";
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DeleteIcon from "@mui/icons-material/Delete";
import { requestResultModel } from "../models/RequestReturnModel";
import LocalSelector from "./components/LocalSelector";
import { isAbortError } from "../services/fetchService";
import { useProductosVenta } from "../hooks/ProductosVentaContext";
import { CatalogoVentaActualizadoDialog } from "../components/CatalogoVentaActualizadoDialog";
import SelectorProductosVenta from "../components/SelectorProductosVenta";


export const gridColumnDefinitions = {
  lineaVentas: {
    'PRODUCTO.PRODUCTO': { Header: 'Producto', Editable: false },
    'PRODUCTO.CONTADO': { Header: '$', Editable: false, IsNumber: true },
    'CANTIDAD': { Header: 'Cantidad', Editable: true, IsNumber: true },
    'DELETE': { Header: 'Eliminar', Editable: false, CustomCell: true }, // Custom column for delete action
  },
  productos: {
    'PRODUCTO': { Header: 'Producto', Editable: false },
    'CODIGO': { Header: 'Codigo', Editable: false },
    'CONTADO': { Header: '$ Unitario', Editable: false, IsNumber: true },
  },
  clientes: {
    'CLIENTE': { Header: 'Cliente', Editable: false },
  },
};

const OrdenDeVenta = () => {
  const { getUser, user } = useUserContext();
  const { showNotification } = useNotification();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [dialogoSeleccion, setDialogoSeleccion] = useState<"cliente" | "producto" | null>(null);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<Cliente | undefined>(undefined);
  const [lineaVenta, setLineaVenta] = useState<LineaVenta[]>([]);
  const [cargandoClientes, setCargandoClientes] = useState(false);
  const [errorClientes, setErrorClientes] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const guardandoRef = useRef(false);
  const {
    productos,
    loading: loadingProductos,
    initialized: productosInicializados,
    error: errorProductos,
    refresh: refreshProductos,
    revision,
  } = useProductosVenta(idLocal);
  const showMessage = useCallback((message: string, severity: "success" | "info" | "warning" | "error") => {
    showNotification(message, severity);
  }, [showNotification]);


  const handleSelectClienteDialog = () => {
    if (cargandoClientes) return;
    setDialogoSeleccion("cliente");
  };

  const handleSelectProductoDialog = () => {
    if (!idLocal) {
      showMessage("Debe seleccionar un local primero", 'error');
      return;
    }

    if (errorProductos) {
      void refreshProductos();
      showMessage("No se pudieron cargar los productos. Reintentando la consulta.", "info");
      return;
    }

    if (loadingProductos || !productosInicializados) {
      showMessage("Cargando productos...", 'info');
      return;
    }

    setDialogoSeleccion("producto");
  };

  const userLoggedIn = useMemo(() => user ?? getUser(), [getUser, user]);

  useEffect(() => {
    if (userLoggedIn) {
      const controller = new AbortController();
      setCargandoClientes(true);
      setErrorClientes(false);
      fetchGetClientes(userLoggedIn, controller.signal).then((response: unknown) => {
        const filas = Array.isArray(response) ? response : [];
        const clientesNormalizados = filas.map((item: any) => ({
          ID: Number(item.ID ?? item.id ?? item.Id ?? item.idCliente ?? item.IdCliente),
          CLIENTE: String(item.CLIENTE ?? item.cliente ?? item.Cliente ?? item.nombre ?? item.Nombre ?? ""),
        })).filter((cliente: Cliente) => Number.isFinite(cliente.ID) && cliente.ID > 0 && cliente.CLIENTE.trim());
        setClientes(clientesNormalizados);
      }).catch(err => {
        if (isAbortError(err)) return;
        setErrorClientes(true);
        showMessage("No se pudieron cargar los clientes.", "error");
      }).finally(() => {
        if (!controller.signal.aborted) setCargandoClientes(false);
      })

      return () => controller.abort();
    }
  }, [showMessage, userLoggedIn]);

  const eventHandler = (params: any) => {
    if (dialogoSeleccion !== "cliente") return;

    const fila = params?.row;
    const idCliente = Number(fila?.ID ?? fila?.idCliente ?? fila?.IdCliente ?? fila?.id);
    const cliente = clientes.find((item) => item.ID === idCliente);
    if (!cliente) {
      showMessage("No se pudo identificar el cliente seleccionado. Vuelva a intentarlo.", "error");
      return;
    }

    setClienteSeleccionado(cliente);
    setDialogoSeleccion(null);
  };

  const handleAgregarProducto = (producto: Producto) => {
    setLineaVenta((actuales) => {
      const id = producto.IDPRODUCTO ?? producto.idProducto;
      const existente = actuales.findIndex((linea) => (linea.producto.IDPRODUCTO ?? linea.producto.idProducto) === id);
      if (existente < 0) return [...actuales, { producto: { ...producto }, cantidad: 1 }];
      return actuales.map((linea, index) => index === existente ? { ...linea, cantidad: linea.cantidad + 1 } : linea);
    });
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedLineaVenta = [...lineaVenta];
    updatedLineaVenta.splice(rowIndex, 1); // Remove the row at the specified index
    setLineaVenta(updatedLineaVenta);
  };

  const guardarVenta = () => {
    if (guardandoRef.current) return;

    if (!userLoggedIn || !clienteSeleccionado || !idLocal || isNaN(Number(idLocal))) {
      showMessage("Error al guardar la venta", 'error');
      return;
    }
    if (lineaVenta.length === 0) {
      showMessage("No hay productos en la venta", 'error');
      return;
    }

    guardandoRef.current = true;
    setGuardando(true);
    fetchGuardarVenta(userLoggedIn!, clienteSeleccionado!.ID, Number(idLocal), lineaVenta).then((res: requestResultModel) => {
      showMessage(res.message, res.success ? 'success' : 'error');
      setLineaVenta([]);
      setClienteSeleccionado(undefined);
    }).catch(err => {
      showMessage("Error al guardar la venta", 'error');
      console.log(err);
    }).finally(() => {
      guardandoRef.current = false;
      setGuardando(false);
    });
  };

  const handleLocalChange = (idLocal: string | undefined) => {
    setIdLocal(idLocal);
  };

  return (
    <Box sx={{ p: 3 }}>
      <CatalogoVentaActualizadoDialog productos={productos} revision={revision} lineas={lineaVenta} setLineas={setLineaVenta} />
      <Typography variant="h4" gutterBottom>
        Orden de Venta
      </Typography>
      {errorClientes && clientes.length === 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          No se pudo cargar la lista de clientes. Actualice la pantalla para volver a intentarlo.
        </Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" gutterBottom>
            Seleccione un Local para la venta:
          </Typography>
        </Grid>
        <Grid item xs={12} sm={2}>
          <LocalSelector onLocalChange={handleLocalChange} />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Button
            onClick={handleSelectClienteDialog}
            variant="contained"
            color="primary"
            fullWidth
            disabled={cargandoClientes}
            sx={{ textTransform: "none", fontWeight: "bold", py: 1.5 }}
            startIcon={cargandoClientes ? <CircularProgress color="inherit" size={18} /> : <PersonIcon />}
          >
            {cargandoClientes ? "Cargando clientes..." : clienteSeleccionado?.CLIENTE ?? "Seleccionar Cliente"}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            onClick={handleSelectProductoDialog}
            variant="contained"
            color="secondary"
            fullWidth
            disabled={loadingProductos || (!productosInicializados && !errorProductos) || !idLocal}
            sx={{ textTransform: "none", fontWeight: "bold", py: 1.5 }}
            startIcon={loadingProductos ? <CircularProgress color="inherit" size={18} /> : <ShoppingCartIcon />}
          >
            {loadingProductos ? "Cargando..." : "Seleccionar Producto"}
          </Button>
        </Grid>
      </Grid>

      {/* Dialog */}
      <Dialog
        open={dialogoSeleccion === "cliente"}
        fullWidth
        onClose={() => setDialogoSeleccion(null)}
      >
        <DialogTitle>
          Seleccionar Cliente
          <Typography variant="body2" color="text.secondary">
            Use los controles de paginación para recorrer los clientes disponibles.
          </Typography>
        </DialogTitle>
        {clientes.length > 0 ? (
          <DynamicGrid
            data={clientes}
            customHeaderMap={gridColumnDefinitions.clientes}
            pageSize={5}
            removeIfNotCustom={true}
            rowClickEventHandler={eventHandler}
          />
        ) : (
          <Alert severity="info" sx={{ m: 2 }}>
            No hay clientes activos disponibles para seleccionar.
          </Alert>
        )}
      </Dialog>
      <SelectorProductosVenta
        open={dialogoSeleccion === "producto"}
        productos={productos}
        lineas={lineaVenta}
        onAgregar={handleAgregarProducto}
        onClose={() => setDialogoSeleccion(null)}
        destino="la venta"
        productosPorPagina={12}
      />
      <div>Orden de Venta para: {clienteSeleccionado?.CLIENTE}</div>
      <DynamicGrid
        data={lineaVenta.map((linea, index) => ({
          ...linea, // Spread the existing properties
          DELETE: "",
        }))} // Add the DELETE property to each row
        customHeaderMap={gridColumnDefinitions.lineaVentas}
        pageSize={5}
        removeIfNotCustom={true}
        customRenderers={{
          DELETE: (row, rowIndex) => (
            <Button
              onClick={() => handleDeleteRow(rowIndex)}
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
            >
              Eliminar
            </Button>
          ),
        }}
      />
      <Box sx={{ mt: 3, textAlign: "right" }}>
        <Button
          onClick={guardarVenta}
          disabled={guardando}
          variant="contained"
          color="success"
          sx={{ textTransform: "none", fontWeight: "bold", px: 4, py: 1.5 }}
          startIcon={guardando ? <CircularProgress color="inherit" size={18} /> : undefined}
        >
          {guardando ? "Guardando..." : "Guardar Venta"}
        </Button>
      </Box>
    </Box>
  );
}

export default OrdenDeVenta;



